import { useRef, useState, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Icon, FileText, LinkIcon, Video, Type, Check, DynamicIsland, BookOpen, PdfDropZone } from '../../../components/ui';
import Input, { InputLabel, InputGroup } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Input';
import { cn } from '../../../lib/cn';
import { extractTextFromPdf } from '../../../lib/pdfTextExtractor';
import CompactLessonFlow from '../../../components/teacher/CompactLessonFlow';

// Steps shown in the Dynamic Island while generating suggestions
// NOTE: These are translated inside the component via useMemo
const SUGGESTION_STEPS_KEYS = [
  'teacherActivityNew.analyzingContent',
  'teacherActivityNew.extractingTitle',
  'teacherActivityNew.generatingTitle',
  'teacherActivityNew.suggestingObjectives',
];

const PDF_EXTRACTION_STEPS_KEYS = [
  'teacherActivityNew.readingPdf',
  'teacherActivityNew.extractingText',
  'teacherActivityNew.processingPages',
];

export default function TeacherActivitiesNew({ errors, lesson_context }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const hasChapters = lesson_context?.chapters?.length > 0;
  const [stimulusType, setStimulusType] = useState(hasChapters ? 'chapter' : 'pdf');
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [stimulusTitle, setStimulusTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [extractingPdf, setExtractingPdf] = useState(false);
  const [pdfExtractedText, setPdfExtractedText] = useState(null);
  const debounceRef = useRef(null);

  const handlePdfFileChange = (f) => {
    setFile(f);
    if (!f) {
      setPdfExtractedText(null);
      setSuggestions(null);
    }
  };

  // Fetch metadata suggestions from the backend
  const fetchSuggestions = useCallback(async (contentText, type) => {
    // Minimum 50 characters required
    if (!contentText || contentText.trim().length < 50) return;

    setLoadingSuggestions(true);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch('/teacher/suggestions/metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          content: contentText,
          stimulus_type: type,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.warn('Failed to fetch metadata suggestions:', error);
      // Fail silently - suggestions are optional
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Debounced trigger for suggestions (1.5s delay)
  const triggerSuggestions = useCallback((contentText, type) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(contentText, type);
    }, 1500);
  }, [fetchSuggestions]);

  // Process PDF file: extract text, then trigger suggestions
  const processPdfFile = useCallback(async (pdfFile) => {
    setExtractingPdf(true);
    setPdfExtractedText(null);
    setSuggestions(null);

    try {
      // Extract text from PDF in the browser
      const result = await extractTextFromPdf(pdfFile, { maxPages: 30 });
      setPdfExtractedText(result.text);

      // Now fetch suggestions using the extracted text
      if (result.text && result.text.trim().length >= 50) {
        setExtractingPdf(false);
        await fetchSuggestions(result.text, 'pdf');
      } else {
        setExtractingPdf(false);
      }
    } catch (error) {
      console.warn('PDF text extraction failed:', error);
      setExtractingPdf(false);
      // Fail silently - suggestions are optional
    }
  }, [fetchSuggestions]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = new FormData();
    // Left blank, the server names it from the stimulus (ActivityTitleService).
    data.append('activity[title]', formData.title.trim());
    data.append('activity[description]', formData.description);

    if (stimulusType === 'chapter' && selectedChapterIds.length > 0) {
      selectedChapterIds.forEach(id => data.append('chapter_ids[]', id));
    } else {
      data.append('stimulus[stimulus_type]', stimulusType);
      data.append('stimulus[title]', stimulusTitle);

      if (stimulusType === 'article') {
        data.append('stimulus[content]', content);
      } else if (stimulusType === 'url' || stimulusType === 'video') {
        data.append('stimulus[url]', url);
      } else if (stimulusType === 'image') {
        data.append('stimulus[content]', content);
      }

      if (file && ['pdf', 'ppt'].includes(stimulusType)) {
        data.append('stimulus[file]', file);
      }
    }

    if (lesson_context?.id) {
      data.append('lesson_id', lesson_context.id);
    }
    if (lesson_context?.from_lesson) {
      data.append('from_lesson', lesson_context.from_lesson);
    }
    if (lesson_context?.slot) {
      data.append('slot', lesson_context.slot);
    }

    router.post('/teacher/activities', data, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  const stimulusTypes = [
    ...(hasChapters ? [{ id: 'chapter', label: t('teacherActivityNew.bookChapter'), icon: BookOpen }] : []),
    { id: 'pdf', label: t('teacherActivityNew.pdfDocument'), icon: FileText },
    { id: 'url', label: t('teacherActivityNew.urlLink'), icon: LinkIcon },
    { id: 'video', label: t('teacherActivityNew.video'), icon: Video },
    { id: 'article', label: t('teacherActivityNew.articleText'), icon: Type },
  ];

  const canSubmit = (
    (stimulusType === 'chapter' && selectedChapterIds.length > 0) ||
    (stimulusType === 'article' && content.trim()) ||
    ((stimulusType === 'url' || stimulusType === 'video') && url.trim()) ||
    (stimulusType === 'image' && content.trim()) ||
    (stimulusType === 'pdf' && file)
  );

  return (
    <TeacherShell scrollTitle={lesson_context ? (<><Link href={lesson_context.from_lesson || `/teacher/courses/${lesson_context.course_id}/units/${lesson_context.unit_id}/lessons/${lesson_context.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{lesson_context.title}</Link>{`  ›  ${t('teacherActivityNew.title')}`}</>) : t('teacherActivityNew.title')}>
      {/* Dynamic Island for PDF extraction and suggestion generation progress */}
      <DynamicIsland
        isActive={extractingPdf || loadingSuggestions}
        steps={(extractingPdf ? PDF_EXTRACTION_STEPS_KEYS : SUGGESTION_STEPS_KEYS).map(k => t(k))}
        stepDuration={extractingPdf ? 800 : 1000}
      />

      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.2), 0 0 30px rgba(59, 130, 246, 0.1);
          }
          50% {
            box-shadow: 0 0 25px rgba(59, 130, 246, 0.4), 0 0 50px rgba(59, 130, 246, 0.2);
          }
        }
        @keyframes cursor-blink {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 8px rgba(59, 130, 246, 0.6), 0 0 16px rgba(59, 130, 246, 0.3);
          }
          50% {
            opacity: 0.3;
            box-shadow: 0 0 4px rgba(59, 130, 246, 0.3), 0 0 8px rgba(59, 130, 246, 0.15);
          }
        }
      `}</style>
      <form onSubmit={handleSubmit}>
        {/* Compact Lesson Flow */}
        {lesson_context && (
          <CompactLessonFlow lessonContext={lesson_context} />
        )}

        {/* Compact header with editable title */}
        <div className="mb-6">
          <div className="flex items-center">
            <div className="relative flex-1 min-w-0 flex items-center">
              {!formData.title && (
                <span
                  className="absolute left-0 w-0.5 h-6 bg-blue-500 rounded-full"
                  style={{
                    animation: 'cursor-blink 1s ease-in-out infinite',
                  }}
                />
              )}
              <input
                type="text"
                value={formData.title}
                onChange={handleChange('title')}
                placeholder={t('teacherActivityNew.untitled')}
                className={cn(
                  "text-xl font-semibold text-gray-900 dark:text-white bg-transparent border-none outline-none focus:ring-0 p-0 placeholder-gray-400 dark:placeholder-gray-500 w-full",
                  !formData.title && "pl-2"
                )}
              />
            </div>
          </div>
          {/* Title suggestion */}
          {!loadingSuggestions && suggestions?.lesson_title && !formData.title && (
            <button
              type="button"
              onClick={() => setFormData((prev) => ({ ...prev, title: suggestions.lesson_title }))}
              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2 transition-colors"
            >
              <Icon icon={Check} size="sm" />
              <span className="truncate max-w-md">{suggestions.lesson_title}</span>
            </button>
          )}
        </div>
        {errors && errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
            <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Teaching Materials (2/3 width) */}
          <div className="lg:col-span-2">
            <Card className="p-6 h-full">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('teacherActivityNew.stimulusHeading')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('teacherActivityNew.stimulusDescription')}
              </p>

              <div className="space-y-6">
                {/* Content Type Selector */}
                <InputGroup>
                  <InputLabel>{t('teacherActivityNew.contentType')}</InputLabel>
                  <div className={cn('grid gap-3', hasChapters ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4')}>
                    {stimulusTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => {
                          setStimulusType(type.id);
                          setContent('');
                          setUrl('');
                          setSelectedChapterIds([]);
                          setSuggestions(null);
                          if (debounceRef.current) {
                            clearTimeout(debounceRef.current);
                          }
                        }}
                        className={cn(
                          'p-3 rounded-lg border-2 text-center transition-all',
                          stimulusType === type.id
                            ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                        )}
                      >
                        <Icon icon={type.icon} size="lg" className="mx-auto mb-1 text-gray-700 dark:text-gray-300" />
                        <span className="text-xs font-medium block text-gray-700 dark:text-gray-300">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </InputGroup>

                {/* Content Area */}
                {stimulusType === 'chapter' && hasChapters && (
                  <div className="space-y-2">
                    {lesson_context.chapters.map((ch) => (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => {
                          setSelectedChapterIds(prev =>
                            prev.includes(ch.id) ? prev.filter(id => id !== ch.id) : [...prev, ch.id]
                          );
                        }}
                        className={cn(
                          'w-full text-left p-4 rounded-lg border-2 transition-all',
                          selectedChapterIds.includes(ch.id)
                            ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                        )}
                      >
                        <p className="font-medium text-gray-900 dark:text-white">
                          Chapter {ch.position + 1}: {ch.title}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>{ch.book_title}</span>
                          {ch.word_count > 0 && <span>{ch.word_count.toLocaleString()} words</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {stimulusType === 'article' && (
                  <div
                    className="rounded-lg"
                    style={!content.trim() ? {
                      animation: 'pulse-glow 2s ease-in-out infinite',
                    } : undefined}
                  >
                    <Textarea
                      id="stimulus-content"
                      value={content}
                      onChange={(e) => {
                        const newContent = e.target.value;
                        setContent(newContent);
                        triggerSuggestions(newContent, stimulusType);
                      }}
                      placeholder={t('teacherActivityNew.articlePlaceholder')}
                      rows={18}
                      className={cn(
                        'font-mono text-sm',
                        !content.trim() && 'border-blue-300 focus:border-blue-400'
                      )}
                    />
                  </div>
                )}

                {(stimulusType === 'url' || stimulusType === 'video') && (
                  <div>
                    <div
                      className="rounded-lg"
                      style={!url.trim() ? {
                        animation: 'pulse-glow 2s ease-in-out infinite',
                      } : undefined}
                    >
                      <Input
                        id="stimulus-url"
                        type="url"
                        value={url}
                        onChange={(e) => {
                          const newUrl = e.target.value;
                          setUrl(newUrl);
                          triggerSuggestions(newUrl, stimulusType);
                        }}
                        placeholder={stimulusType === 'video' ? 'https://youtube.com/watch?v=...' : 'https://example.com/article'}
                        className={cn(
                          'text-lg',
                          !url.trim() && 'border-blue-300 focus:border-blue-400'
                        )}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {stimulusType === 'video'
                        ? t('teacherActivityNew.videoPlaceholder')
                        : t('teacherActivityNew.urlPlaceholder')}
                    </p>
                  </div>
                )}

                {stimulusType === 'image' && (
                  <div>
                    <Input
                      id="stimulus-image"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="text-lg"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {t('teacherActivityNew.imagePlaceholder')}
                    </p>
                  </div>
                )}

                {stimulusType === 'pdf' && (
                  <PdfDropZone
                    file={file}
                    onFileChange={handlePdfFileChange}
                    onFileSelected={processPdfFile}
                  />
                )}

                {/* Source Title */}
                <InputGroup>
                  <InputLabel htmlFor="stimulus-title">{t('teacherActivityNew.sourceTitle')}</InputLabel>
                  <Input
                    id="stimulus-title"
                    value={stimulusTitle}
                    onChange={(e) => setStimulusTitle(e.target.value)}
                    placeholder={t('teacherActivityNew.sourceTitlePlaceholder')}
                  />

                  {/* Source title suggestion */}
                  {suggestions?.source_title && !stimulusTitle && (
                    <button
                      type="button"
                      onClick={() => setStimulusTitle(suggestions.source_title)}
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2 transition-colors"
                    >
                      <Icon icon={Check} size="sm" />
                      <span className="truncate max-w-sm">{suggestions.source_title}</span>
                    </button>
                  )}
                </InputGroup>
              </div>
            </Card>
          </div>

          {/* Right Column: Activity Details (1/3 width) */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('teacherActivityNew.activityDetails')}</h2>

              <InputGroup>
                <InputLabel htmlFor="description">{t('teacherActivityNew.objectives')}</InputLabel>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  placeholder={t('teacherActivityNew.objectivesPlaceholder')}
                  rows={4}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('teacherActivityNew.objectivesHelp')}
                </p>
                {/* Objectives suggestion */}
                {suggestions?.objectives && !formData.description && (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, description: suggestions.objectives }))}
                    className="flex items-start gap-1.5 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2 transition-colors text-left"
                  >
                    <Icon icon={Check} size="sm" className="mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{suggestions.objectives}</span>
                  </button>
                )}
              </InputGroup>

              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting || !canSubmit}
                  className="w-full"
                >
                  {isSubmitting ? t('teacherActivityNew.creating') : t('teacherActivityNew.createActivity')}
                </Button>

                <Link href={lesson_context?.from_lesson || "/teacher/dashboard"} className="block mt-3">
                  <Button type="button" variant="ghost" className="w-full">
                    {t('common.cancel')}
                  </Button>
                </Link>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
                  {t('teacherActivityNew.nextGenerateQuestions')}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </TeacherShell>
  );
}

